import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateIntegrationQuery,
	useGetExternalIntegrationQuery,
	usePostRedtailAuthMutation,
} from '../../../../queries';
import { useDisconnectUserFromIntegrationMutation } from '../../../../queries/Integrations/useDisconnectUserFromIntegrationMutation';
import redtailLogo from '../../../assets/logo_redtail.png';
import { Button } from '../../../components-v2/Button';
import { Input } from '../../../components-v2/Input';
import { DisconnectSyncUserModal } from '../../../components/integrations/DisconnectSyncUserModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { DangerIcon } from '../../../components/svgs/icons/DangerIcon';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { nameCircles, white } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

enum TabValues {
	Connect = 'connect',
	Manage = 'manage',
}

enum IntegrationState {
	NotConnected = 'not-connected',
	Connected = 'connected',
}

export const RedtailV2: React.FC = () => {
	const history = useHistory();
	const userSession = useUserSession();
	const params = useParams<{ tab: TabValues }>();
	const [isDisableModalOpen, setIsDisableModalOpen] = React.useState(false);
	const [username, setUsername] = React.useState('');
	const [password, setPassword] = React.useState('');

	const disableIntegrationMutation = useDisconnectUserFromIntegrationMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: () => {
			toast.success('Integration disabled successfully');
			invalidateIntegrationQuery({ provider: Api.IntegrationProvider.Redtail });
			history.push('/integrations');
		},
	});

	const authMutation = usePostRedtailAuthMutation({
		onError: error => {
			toast.error(error.systemMessage);
			setPassword('');
		},
		onSuccess: () => {
			userSession.resolvePendingAction(Api.PendingActions.ConnectRedtailV2);
			invalidateIntegrationQuery({ provider: Api.IntegrationProvider.Redtail });
			toast.success('Successfully connected to Redtail.');
			setPassword('');
		},
	});

	const integrationQuery = useGetExternalIntegrationQuery({
		provider: Api.IntegrationProvider.Redtail,
	});

	const changeTab = (value: string) => {
		history.replace(`/integrations/${Api.IntegrationProvider.Redtail}/${value}`);
	};

	const integrationState = React.useMemo(() => {
		const userConnections = integrationQuery.data?.configuration?.userConnections || [];
		const userConnection = userConnections.find(connection => connection.userId === userSession.user.id);
		return userConnection?.isConnected ? IntegrationState.Connected : IntegrationState.NotConnected;
	}, [integrationQuery.data, userSession.user.id]);

	React.useEffect(() => {
		if (!integrationQuery.data) {
			return;
		}
		if (integrationState === IntegrationState.Connected) {
			changeTab(TabValues.Manage);
		} else {
			changeTab(TabValues.Connect);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [integrationQuery.data, integrationState]);

	const connect = () => {
		if (!username.trim() || !password.trim()) {
			return;
		}
		authMutation.mutate({ username: username.trim(), password });
	};

	const onDisable = () => {
		disableIntegrationMutation.mutate(Api.ExternalIntegrationType.Redtail);
	};

	if (integrationQuery.isPending || authMutation.isPending) {
		return <LoadingSpinner type='large' className={css(bs.absoluteCenter)} />;
	}

	if (!params.tab) {
		changeTab(TabValues.Connect);
	}

	const isConnected = integrationState === IntegrationState.Connected;
	const hasAuthError = !!authMutation.error;
	const isSubmitDisabled = !username.trim() || !password.trim();

	return (
		<div className={css(styleSheet.container)}>
			<figure className={css(integrationsStyleSheet.logo, integrationsStyleSheet.headerLogo)}>
				<img src={redtailLogo} alt='Redtail Logo' />
			</figure>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
				Levitate can sync with Redtail, and pull client information, including names, email, phone number, birthdays,
				anniversaries, and more. We can also push our notes into Redtail.
			</p>
			<Tabs.Root className={`tab-view ${css(styleSheet.tabView)}`} value={params.tab} onValueChange={changeTab}>
				<Tabs.List className={`tab-view-tabbar-tabs ${css(styleSheet.tabViewTabs)}`}>
					<Tabs.Trigger
						value={TabValues.Connect}
						className={`tab-view-tabbar-item ${params.tab === TabValues.Connect ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''} ${css(styleSheet.tabViewItem)}`}
					>
						<SuccessCheckIcon fillColor={isConnected ? undefined : nameCircles} ringColor='#F9F9F9' />
						<span>Connect</span>
					</Tabs.Trigger>

					<Tabs.Trigger
						value={TabValues.Manage}
						className={`tab-view-tabbar-item ${params.tab === TabValues.Manage ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''} ${css(styleSheet.tabViewItem)}`}
					>
						<span>Manage</span>
					</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value={TabValues.Connect} className='tab-view-content'>
					{isConnected ? (
						<div className={css(styleSheet.section)}>
							<p className={css(integrationsStyleSheet.bodyText)}>
								If you wish to disable this integration for your user, click Disconnect.
							</p>
							<Button
								variant='primary'
								onClick={ev => {
									ev.preventDefault();
									if (integrationQuery.data?.configuration?.syncUserId === userSession.user.id) {
										setIsDisableModalOpen(true);
									} else {
										onDisable();
									}
								}}
							>
								Disconnect
							</Button>
						</div>
					) : (
						<div className={css(styleSheet.section)}>
							<p className={css(integrationsStyleSheet.bodyText)}>
								To get started, please sign into your Redtail account. Levitate does not store your username and
								password. Your credentials are exchanged with Redtail directly for a key, which is stored securely.
							</p>
							<div className={css(styleSheet.signInContainer)}>
								<div className={css(styleSheet.signInForm)}>
									<div className={css(styleSheet.inputWrapper)}>
										<label htmlFor='redtail-username' className={css(styleSheet.signInFormFieldLabel)}>
											Username
										</label>
										<div className={css(styleSheet.signInFormField)}>
											<Input
												autoComplete='off'
												autoFocus={true}
												id='redtail-username'
												onChange={ev => setUsername(ev.target.value)}
												type='text'
												value={username}
												styles={[styleSheet.textInput]}
												aria-invalid={hasAuthError}
											/>
										</div>
									</div>
									<div className={css(styleSheet.inputWrapper)}>
										<label htmlFor='redtail-password' className={css(styleSheet.signInFormFieldLabel)}>
											Password
										</label>
										<div className={css(styleSheet.signInFormField)}>
											<Input
												autoComplete='off'
												id='redtail-password'
												onChange={ev => setPassword(ev.target.value)}
												onKeyDown={ev => {
													if (ev.key === 'Enter') {
														connect();
													}
												}}
												type='password'
												value={password}
												styles={[styleSheet.textInput]}
												aria-invalid={hasAuthError}
												aria-describedby={hasAuthError ? 'redtail-auth-error' : undefined}
											/>
											{hasAuthError && <DangerIcon backgroundColor={white} />}
										</div>
										{hasAuthError ? (
											<span id='redtail-auth-error' role='alert' className={css(styleSheet.signInFormFieldError)}>
												{authMutation.error?.systemMessage ||
													'Unable to connect to Redtail. Please check your credentials and try again.'}
											</span>
										) : null}
									</div>
								</div>
								<div>
									<Button variant='primary' disabled={isSubmitDisabled} onClick={connect}>
										Connect
									</Button>
								</div>
							</div>
						</div>
					)}
				</Tabs.Content>

				<Tabs.Content value={TabValues.Manage} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						<p className={css(integrationsStyleSheet.bodyText)}>
							Once you are connected, your Redtail data will show up in the contacts tab.
						</p>
						<Link to='/people' className={css(bs.ctaButton)}>
							Contacts
						</Link>
					</div>
				</Tabs.Content>
			</Tabs.Root>

			<DisconnectSyncUserModal
				integrationDisplayName='Redtail'
				isDisableModalOpen={isDisableModalOpen}
				onDisable={onDisable}
				setIsDisableModalOpen={setIsDisableModalOpen}
			/>
		</div>
	);
};
